import { useState } from 'react'
import { fbt } from 'fbtee'
import { cancelButtonLabel } from '../lib/commonStrings'
import { Input } from './ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description: React.ReactNode
  confirmLabel: React.ReactNode
  onConfirm: () => void
  destructive?: boolean
  // When set, the user must type this word (case-insensitive) before the
  // confirm button enables — a guard for irreversible destructive actions.
  confirmKeyword?: string
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  destructive = true,
  confirmKeyword,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('')

  function handleOpenChange(next: boolean) {
    if (!next) setTyped('')
    onOpenChange(next)
  }

  const matches =
    !confirmKeyword || typed.trim().toLowerCase() === confirmKeyword.trim().toLowerCase()

  function handleConfirm(e: React.MouseEvent) {
    // Defense in depth: never fire onConfirm unless the keyword matches, even
    // if the disabled state were somehow bypassed.
    if (!matches) {
      e.preventDefault()
      return
    }
    onConfirm()
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {confirmKeyword && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              <fbt desc="Prompt telling the user to type a specific word to confirm a destructive action">
                Type{' '}
                <fbt:param name="keyword">
                  <span className="font-semibold text-foreground">{confirmKeyword}</span>
                </fbt:param>{' '}
                to confirm
              </fbt>
            </p>
            <Input
              value={typed}
              onChange={e => setTyped(e.target.value)}
              autoFocus
              autoComplete="off"
              aria-label={fbt(
                'Type the confirmation word',
                'Accessible label for the type-to-confirm input',
              ).toString()}
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel className="cursor-pointer">
            {cancelButtonLabel()}
          </AlertDialogCancel>
          {/* The variant, not a bg class: Slot joins classes without tailwind-merge,
              so a className bg loses to the variant's bg-primary. */}
          <AlertDialogAction
            disabled={!matches}
            variant={destructive ? 'destructive' : 'default'}
            className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleConfirm}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
